/**
 * Push-to-talk dictation through the Web Speech API where the browser offers it (desktop
 * Chrome and Edge). Quest Browser does not, so the form falls back to the keyboard.
 */
interface SpeechResultEvent {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: SpeechResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechCtor = new () => SpeechRecognitionLike;

function ctor(): SpeechCtor | undefined {
  const w = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export function dictationAvailable(): boolean {
  return typeof window !== 'undefined' && !!ctor();
}

export interface DictationHandlers {
  onInterim(text: string): void;
  onFinal(text: string): void;
  onEnd(error?: string): void;
}

export class Dictation {
  private rec: SpeechRecognitionLike | undefined;
  private stopping = false;

  constructor(private readonly handlers: DictationHandlers) {}

  get active(): boolean {
    return !!this.rec;
  }

  start(lang = 'en-GB'): boolean {
    const C = ctor();
    if (!C || this.rec) return false;
    const rec = new C();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang;
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]!;
        const t = r[0].transcript;
        if (r.isFinal) this.handlers.onFinal(t.trim());
        else interim += t;
      }
      this.handlers.onInterim(interim.trim());
    };
    rec.onerror = (e) => {
      const error = e.error === 'no-speech' ? undefined : e.error;
      this.rec = undefined;
      this.handlers.onEnd(error);
    };
    rec.onend = () => {
      if (!this.rec) return;
      this.rec = undefined;
      this.handlers.onEnd(this.stopping ? undefined : undefined);
    };
    this.rec = rec;
    this.stopping = false;
    try {
      rec.start();
    } catch {
      this.rec = undefined;
      return false;
    }
    return true;
  }

  stop(): void {
    if (!this.rec) return;
    this.stopping = true;
    const rec = this.rec;
    this.rec = undefined;
    try {
      rec.stop();
    } catch {
      /* already stopped */
    }
    this.handlers.onEnd();
  }
}
